import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { lessonApi, aiApi } from '../api/client';
import { Lesson, ContentBlock } from '../types';
import { useAuth } from '../context/AuthContext';
import {
  CheckCircle, MessageCircle, ChevronLeft,
  Clock, Star
} from 'lucide-react';
import toast from 'react-hot-toast';

export function LessonPage() {
  const { courseId, lessonId }        = useParams();
  const { user }                      = useAuth();
  const navigate                      = useNavigate();
  const [lesson, setLesson]           = useState<Lesson | null>(null);
  const [completing, setCompleting]   = useState(false);
  const [completed, setCompleted]     = useState(false);
  const [aiQuestion, setAiQuestion]   = useState('');
  const [aiAnswer, setAiAnswer]       = useState('');
  const [aiLoading, setAiLoading]     = useState(false);
  const [showAi, setShowAi]           = useState(false);

 useEffect(() => {
  // Log to confirm we have the lessonId
  console.log('lessonId from params:', lessonId);
  
  if (!lessonId) {
    console.error('No lessonId in URL params');
    return;
  }
  
  lessonApi.getLesson(lessonId)
    .then(res => {
      console.log('Lesson loaded:', res.data);
      setLesson(res.data);
      setCompleted(res.data.progressStatus === 'COMPLETED');
    })
    .catch((err) => {
      console.error('Lesson load error:', err);
      toast.error('Failed to load lesson');
    });
}, [lessonId]);

  const handleComplete = async () => {
    if (!lessonId || completing || completed) return;
    setCompleting(true);
    try {
      const res = await lessonApi.complete(lessonId);
      setCompleted(true);
      toast.success(res.data);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not mark complete');
    } finally {
      setCompleting(false);
    }
  };

  const handleAskAi = async () => {
    if (!aiQuestion.trim()) return;
    setAiLoading(true);
    setAiAnswer('');
    try {
      const res = await aiApi.ask(aiQuestion, lessonId);
      setAiAnswer(res.data.answer);
    } catch {
      setAiAnswer('Study Buddy is unavailable right now.');
    } finally {
      setAiLoading(false);
    }
  };

  // Render a single content block
  const renderBlock = (block: ContentBlock, idx: number) => {
    switch (block.type) {
      case 'heading':
        return (
          <h2 key={idx}
              className="text-xl font-black text-white mt-8 mb-3">
            {block.text}
          </h2>
        );
      case 'paragraph':
        return (
          <p key={idx}
             className="text-gray-300 leading-relaxed mb-4 text-sm">
            {block.text}
          </p>
        );
      case 'callout':
        const colors: Record<string, string> = {
          info:    'border-blue-400/40 bg-blue-400/5 text-blue-300',
          example: 'border-green-400/40 bg-green-400/5 text-green-300',
          warning: 'border-yellow-400/40 bg-yellow-400/5 text-yellow-300',
          tip:     'border-purple-400/40 bg-purple-400/5 text-purple-300',
        };
        const calloutClass = colors[block.variant || 'info'] || colors.info;
        return (
          <div key={idx}
               className={`border rounded-xl px-4 py-3 mb-4 text-sm
                           ${calloutClass}`}>
            {block.text}
          </div>
        );
      case 'table':
        return (
          <div key={idx} className="overflow-x-auto mb-4">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-800">
                  {block.headers?.map((h, i) => (
                    <th key={i}
                        className="text-left px-4 py-2 text-green-400
                                   font-semibold border border-gray-700">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows?.map((row, ri) => (
                  <tr key={ri} className={ri % 2 === 0 ? 'bg-gray-900' : 'bg-gray-800/50'}>
                    {row.map((cell, ci) => (
                      <td key={ci}
                          className="px-4 py-2 text-gray-300
                                     border border-gray-700">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case 'video':
        return (
          <div key={idx} className="mb-4 rounded-xl overflow-hidden bg-gray-800">
            <video
              src={block.url}
              controls
              className="w-full"
            />
          </div>
        );
      case 'code':
        return (
          <pre key={idx}
               className="bg-gray-800 border border-gray-700 rounded-xl
                          p-4 mb-4 text-sm text-green-400 overflow-x-auto
                          font-mono leading-relaxed">
            {block.text}
          </pre>
        );
      default:
        return null;
    }
  };

  if (!lesson) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-8 text-gray-500">
        Loading lesson...
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      {/* Back button */}
      <button
        onClick={() => navigate(`/courses/${courseId}`)}
        className="flex items-center gap-2 text-gray-500
                   hover:text-white transition-colors text-sm mb-6"
      >
        <ChevronLeft size={16} />
        Back to course
      </button>

      {/* Lesson header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-xs bg-gray-800 text-gray-400
                           px-2 py-1 rounded-full">
            {lesson.lessonType}
          </span>
          <span className="text-xs text-gray-500 flex items-center gap-1">
            <Clock size={10} /> {lesson.estimatedMins} min
          </span>
          <span className="text-xs text-green-400 flex items-center gap-1">
            <Star size={10} /> +{lesson.xpReward} XP
          </span>
        </div>
        <h1 className="text-2xl font-black text-white">
          {lesson.title}
        </h1>


{(user?.role === 'TEACHER' || user?.role === 'SUPER_ADMIN') && (
  <button
    onClick={() => navigate(`/lessons/${lessonId}/edit`)}
    className="mt-2 text-xs text-gray-500 hover:text-green-400
               transition-colors flex items-center gap-1"
  >
    ✏️ Edit lesson content
  </button>
)}

        {completed && (
          <div className="flex items-center gap-2 mt-2
                          text-green-400 text-sm">
            <CheckCircle size={14} />
            Completed
          </div>
        )}
      </div>

      {/* Content blocks */}
      <div className="prose-sm">
        {lesson.contentBlocks.map((block, idx) =>
          renderBlock(block, idx)
        )}
      </div>

      {/* AI Summary */}
      {lesson.aiSummary && (
        <div className="mt-6 border border-purple-400/30
                        bg-purple-400/5 rounded-xl p-4">
          <div className="text-xs font-bold text-purple-400 mb-2">
            🤖 AI Summary
          </div>
          <p className="text-gray-300 text-sm leading-relaxed">
            {lesson.aiSummary}
          </p>
        </div>
      )}

      {/* Study Buddy inline */}
      <div className="mt-6 border border-gray-800 rounded-xl overflow-hidden">
        <button
          onClick={() => setShowAi(!showAi)}
          className="w-full flex items-center gap-3 p-4
                     hover:bg-gray-900 transition-colors text-left"
        >
          <MessageCircle size={16} className="text-green-400" />
          <span className="font-semibold text-white text-sm">
            Ask Study Buddy about this lesson
          </span>
        </button>

        {showAi && (
          <div className="border-t border-gray-800 p-4 bg-gray-900/50">
            <div className="flex gap-2 mb-3">
              <input
                value={aiQuestion}
                onChange={e => setAiQuestion(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAskAi()}
                placeholder="Ask anything about this lesson..."
                className="flex-1 bg-gray-800 border border-gray-700
                           rounded-lg px-3 py-2 text-white text-sm
                           focus:outline-none focus:border-green-400"
              />
              <button
                onClick={handleAskAi}
                disabled={aiLoading}
                className="bg-green-400 text-gray-950 font-bold
                           px-4 py-2 rounded-lg text-sm
                           hover:bg-green-300 disabled:opacity-50"
              >
                {aiLoading ? '...' : 'Ask'}
              </button>
            </div>
            {aiAnswer && (
              <div className="bg-gray-800 rounded-xl p-4 text-sm
                              text-gray-200 leading-relaxed">
                {aiAnswer}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Complete button */}
      {user?.role === 'STUDENT' && (
        <div className="mt-6">
          <button
            onClick={handleComplete}
            disabled={completed || completing}
            className={`w-full font-bold py-3 rounded-xl text-sm
              transition-colors
              ${completed
                ? 'bg-green-400/20 text-green-400 border border-green-400/30 cursor-default'
                : 'bg-green-400 text-gray-950 hover:bg-green-300'
              }`}
          >
            {completed
              ? '✅ Lesson Completed'
              : completing
              ? 'Marking complete...'
              : 'Mark as Complete — Earn ' + lesson.xpReward + ' XP'
            }
          </button>
        </div>
      )}
    </div>
  );
}